/**
 * AICoachPage
 *
 * AI Coach session page with new design:
 * - Progress tabs at top: "Task Performance | Communication | Everyday Work"
 * - 50/50 split on desktop, stacked on mobile
 * - AI Coach info and assistant presence in right panel
 * - Continue button at bottom with states for AI speaking and content preparation
 *
 * Responsive breakpoints:
 * - xs (0-600): Stacked layout, smaller elements
 * - sm (600-900): Stacked layout with more spacing
 * - md (900-1200): Side-by-side with adjusted proportions
 * - lg (1200+): Full desktop layout (50/50 split)
 */

import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Typography, CircularProgress } from '@mui/material'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useAICoachPageState } from './hooks/useAICoachPageState'
import { AI_COACH_COLORS } from './constants'
import {
  AICoachVideoPanel,
  AICoachProgressTabs,
  AICoachParticipantList,
  AICoachInfoPanel,
  AICoachContinueButton,
  AICoachReplayButton
} from './components'
import { useGlobalWhereby } from '../../../../shared/hooks/useGlobalWhereby'
import { broadcastLeaveGameCall } from '../../../../shared/hooks/wherebyCrossTab'
import { useWherebyCallOwnership } from '../../../../shared/hooks/useWherebyCallOwnership'
import { buildAiCoachCallScope } from '../../../../shared/hooks/wherebyCallScopes'

interface AICoachPageProps {
  sessionId?: string
}

export const AICoachPage: React.FC<AICoachPageProps> = ({ sessionId }) => {
  const { t } = useTranslation()
  const state = useAICoachPageState({ sessionId })
  const location = useLocation()
  const { killCall } = useGlobalWhereby()

  // Kill the global floating widget on mount.
  // AI Coach has its own WherebyCallSurface — running both simultaneously
  // causes the user to appear in two Whereby rooms at once.
  useEffect(() => {
    killCall()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Reaching the AI coach means the game is over for this user. Tell sibling tabs of this browser to drop
  // any lingering game call for this team so a forgotten game tab can't keep the room alive and block the
  // analysis. Team-scoped (fires once the team is known) so we never touch an unrelated live game.
  const gameTeamId = state.sessionData?.teamId ?? state.effectiveSessionData?.teamId
  useEffect(() => {
    if (gameTeamId) broadcastLeaveGameCall({ teamId: gameTeamId })
  }, [gameTeamId])

  // One live Whereby connection per user: this debrief call goes through the same claim as every other
  // call surface, so a game call left open in another tab/device is moved here only after the user says so.
  const session = state.effectiveSessionData
  const ownershipScope = useMemo(
    () => buildAiCoachCallScope({ session, teamName: state.teamName, route: location.pathname }),
    [session, state.teamName, location.pathname]
  )

  // The debrief room exists and the user has not hung up — i.e. this page wants to be in the call.
  const wantsToBeInTheCall = Boolean(state.isWherebyRoomReady && state.roomUrl && !state.hasUserLeftCall)
  const ownership = useWherebyCallOwnership({ enabled: wantsToBeInTheCall, scope: ownershipScope })
  // Join only after room ownership and the local Whereby identity are stable.
  const hasStableWherebyIdentity = Boolean(state.currentUserId && state.displayName)
  const canJoinTheDebriefCall = wantsToBeInTheCall && ownership.canConnect && hasStableWherebyIdentity

  // Loading state
  if (state.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        }}
      >
        <CircularProgress size={48} sx={{ color: INTERAX_COLORS.sun }} />
        <Typography sx={{ fontSize: '16px', color: INTERAX_COLORS.white }}>{t('aiCoach.page.loading')}</Typography>
      </Box>
    )
  }

  // Error state
  if (state.hasError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2,
          padding: { xs: 2, sm: 3 }
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '16px', sm: '18px' },
            fontWeight: 600,
            color: INTERAX_COLORS.white,
            textAlign: 'center'
          }}
        >
          {t('aiCoach.page.error')}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '12px', sm: '14px' },
            color: INTERAX_COLORS.thunderGrey,
            textAlign: 'center'
          }}
        >
          {state.hasError instanceof Error ? state.hasError.message : ''}
        </Typography>
      </Box>
    )
  }

  // No session state
  if (state.hasNoSession && !state.enabledDevAICoach) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2,
          padding: { xs: 2, sm: 3 }
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '16px', sm: '18px' },
            fontWeight: 600,
            color: INTERAX_COLORS.white,
            textAlign: 'center'
          }}
        >
          {t('aiCoach.page.noSession')}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '12px', sm: '14px' },
            color: INTERAX_COLORS.thunderGrey,
            textAlign: 'center',
            maxWidth: '600px'
          }}
        >
          {t('aiCoach.page.noSessionDescription')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        height: '100vh',
        maxHeight: '100vh',
        backgroundColor: AI_COACH_COLORS.pageBackground,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: { xs: 1.5, sm: 2, md: 2.5, lg: 2.5 }
      }}
    >
      {/* Progress Tabs */}
      <AICoachProgressTabs currentPart={state.currentPart} />

      {/* Main Split Layout - Stacked on mobile, side-by-side on desktop */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 1.5, sm: 2, md: 2.5, lg: 2.5 },
          flex: 1,
          minHeight: 0,
          overflow: { xs: 'auto', md: 'hidden' }
        }}
      >
        {/* Left: the debrief call (or the one-call-per-user prompt) */}
        <AICoachVideoPanel
          canJoinTheCall={canJoinTheDebriefCall}
          roomUrl={state.roomUrl}
          userId={state.currentUserId}
          displayName={state.displayName}
          teamName={state.teamName}
          hasUserLeftCall={state.hasUserLeftCall}
          ownershipStatus={ownership.status}
          onAssistantPresenceChange={state.setIsAssistantInCall}
          onLeaveCall={state.handleLeaveToDashboard}
          onConfirmTakeover={ownership.confirmTakeover}
          onRetryOwnership={ownership.retry}
        />

        {/* Right: Participants - No glass effect */}
        <Box
          sx={{
            width: { xs: '100%', md: '40%', lg: '40%' },
            padding: { xs: 1.5, sm: 2, md: 2.5, lg: 2.5 },
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 'auto', md: 0 },
            overflow: { xs: 'visible', md: 'auto' }
          }}
        >
          <AICoachParticipantList
            participants={state.participants}
            participantNames={state.participantNames}
            currentUserId={state.currentUserId}
          />

          <Box
            sx={{
              width: '100%',
              paddingBottom: '16px',
              mt: { xs: 1.5, sm: 2 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <AICoachContinueButton
              currentPart={state.currentPart}
              isAISpeaking={state.isAISpeaking}
              isButtonLoading={state.isButtonLoading}
              isContentReady={state.isContentReady}
              isPreparingContent={state.isPreparingContent}
              onContinue={state.handleContinue}
              onLeave={state.handleLeaveToDashboard}
            />
            <AICoachReplayButton
              canReplay={state.canReplay}
              isReplaying={state.isReplaying}
              isAISpeaking={state.isAISpeaking}
              onReplay={state.handleReplay}
            />
          </Box>

          {state.isPreparingContent ? (
            // Shown inline on touch screens (no hover); on desktop the button tooltip carries the same message.
            <Typography
              sx={{
                display: { xs: 'block', sm: 'block', md: 'none' },
                width: '100%',
                textAlign: 'center',
                color: INTERAX_COLORS.white70,
                fontSize: { xs: 13, sm: 14 },
                mb: 1,
                px: 2
              }}
            >
              {t('aiCoach.tooltips.preparingContentHint')}
            </Typography>
          ) : null}

          <AICoachInfoPanel
            currentPart={state.currentPart}
            isAISpeaking={state.isAISpeaking}
            isAssistantInCall={state.isAssistantInCall}
          />
        </Box>
      </Box>
    </Box>
  )
}
